import React, { useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Spin,
  Select,
  Divider,
  InputNumber,
  Space,
  Tag,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { save } from "../../../features/save/saveSlice";
import BreadCrumb from "../../../layout/BreadCrumb";
import {
  fetchData,
  fetchLocation,
  fetchSubCounties,
  fetchSubLocation,
  fetchSubsidySource,
} from "../../../features/fetch/fetchSlice";
import { FldControlStatusData, rvrTrainingStatusData } from "../../../data";
import dayjs from "dayjs";
import { normalizeDateToLocalYear } from "../../../utils";

const { TextArea } = Input;
const RiverTrainingAdd = () => {
  const urlTreatType = "treatment_type/fetch_treatmenttypes";
  const urlAuxiliary = "auxiliary/fetch_auxiliarys";
  const urlWtrSrce = "water_sources/fetch_watersourcess";
  const urlCrop = "main_crop/fetch_maincrops";
  const urlPathway = "api/v2/irrigationPathway/get";
  const urlAgency = "api/v2/agency/get";

  const urlWard = "ward/fetch_wards";
  const urlConstituency = "constituency/fetch_constituencys";

  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { saving } = useSelector((state) => state.save);
  const {
    counties,
    subCounties,
    locations,
    subLocations,
    ward,
    constituency,
    subsidySources,
  } = useSelector((state) => state.fetch);
  const {
    activeObjRiverTraining,
    dataCrop,
    dataPathway,
    dataAgency,
    dataConstituency,
    dataWard,
  } = useSelector((state) => ({
    activeObjRiverTraining: state.obj.activeObj["riverTraining"],
    dataWtrSrc: state.fetch.data[urlWtrSrce] || [],
    dataTreatType: state.fetch.data[urlTreatType] || [],
    dataAuxiliary: state.fetch.data[urlAuxiliary] || [],
    dataCrop: state.fetch.data[urlCrop] || [],
    dataPathway: state.fetch.data[urlPathway] || [],
    dataAgency: state.fetch.data[urlAgency] || [],

    dataWard: state.fetch.data[urlWard] || [],
    dataConstituency: state.fetch.data[urlConstituency] || [],
  }));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleCancel() {
    navigate("/rob/river-training");
  }

  const [countyName, setCountyName] = useState(
    activeObjRiverTraining?.countyName ?? ""
  );

  const onFinish = async (data) => {
    data.riverId = activeObjRiverTraining?.riverId
      ? activeObjRiverTraining?.riverId
      : null;
    data.riverCropsId = Array.isArray(data?.riverCropsId)
      ? data?.riverCropsId.join(",")
      : data?.riverCropsId;

    data.riverPathwayId = Array.isArray(data?.riverPathwayId)
      ? data?.riverPathwayId.join(",")
      : data?.riverPathwayId;

    data.riverSourceFundsId = Array.isArray(data?.riverSourceFundsId)
      ? data?.riverSourceFundsId.join(",")
      : data?.riverSourceFundsId;
    data.riverStartDate = normalizeDateToLocalYear(data?.riverStartDate);
    data.riverEndDate = normalizeDateToLocalYear(data?.riverEndDate);
    const res = await dispatch(
      save({
        ...data,
        url: "river_training/save_rivertraining",
      })
    );
    if (res?.payload?.success) {
      await toast.success(res?.payload?.message);
      await form.resetFields();
      await handleCancel();
    } else {
      toast.error(res?.payload?.message);
    }
  };

  const [riverCountyId, setRiverCountyId] = useState(
    activeObjRiverTraining?.riverCountyId ?? ""
  );
  const [riverConstId, setRiverConstId] = useState(
    activeObjRiverTraining?.riverConstId ?? ""
  );
  const [riverSubCountyId, setRiverSubCountyId] = useState(
    activeObjRiverTraining?.riverSubCountyId ?? ""
  );
  const [riverLocationId, setRiverLocationId] = useState(
    activeObjRiverTraining?.riverLocationId ?? ""
  );
  const [riverSubLocationId, setRiverSubLocationId] = useState(
    activeObjRiverTraining?.riverSubLocationId ?? ""
  );
  const [riverWardId, setRiverWardId] = useState(
    activeObjRiverTraining?.riverWardId ?? ""
  );

  const handleRiverCountyChange = async (value) => {
    setRiverCountyId(value);
    setRiverConstId("");
    setRiverSubCountyId("");
    setRiverLocationId("");
    setRiverSubLocationId("");
    setRiverWardId("");

    form.setFieldsValue({
      riverCountyId: value,
      riverConstId: "",
      riverSubCountyId: "",
      riverLocationId: "",
      riverSubLocationId: "",
      riverWardId: "",
    });

    await dispatch(fetchSubCounties({ subCountyId: value }));
    await dispatch(
      fetchData({
        url: urlConstituency,
        constCountyId: value,
      })
    );
  };

  const handleRiverConstituencyChange = async (value) => {
    setRiverConstId(value);
    setRiverWardId("");

    form.setFieldsValue({
      riverConstId: value,
      riverWardId: "",
    });

    await dispatch(
      fetchData({
        url: urlWard,
        wdConstId: value,
      })
    );
  };

  const handleRiverSubCountyChange = async (value) => {
    setRiverSubCountyId(value);
    setRiverLocationId("");
    setRiverSubLocationId("");

    form.setFieldsValue({
      riverSubCountyId: value,
      riverLocationId: "",
      riverSubLocationId: "",
    });

    await dispatch(fetchLocation({ locSubId: value }));
  };

  const handleRiverLocationChange = async (value) => {
    setRiverLocationId(value);
    setRiverSubLocationId("");

    form.setFieldsValue({
      riverLocationId: value,
      riverSubLocationId: "",
    });

    await dispatch(fetchSubLocation({ slocLocId: value }));
  };

  const handleRiverSubLocationChange = (value) => {
    setRiverSubLocationId(value);
    form.setFieldsValue({ riverSubLocationId: value });
  };

  const handleRiverWardChange = (value) => {
    setRiverWardId(value);
    form.setFieldsValue({ riverWardId: value });
  };

  const handleDatesChange = (_, allValues) => {
    const { riverStartDate, riverEndDate } = allValues;
    if (riverStartDate && riverEndDate) {
      const diff = dayjs(riverEndDate)?.diff(dayjs(riverStartDate), "day");
      form.setFieldsValue({
        riverTimeFrame: `${diff} day${diff !== 1 ? "s" : ""}`,
      });
    } else {
      form.setFieldsValue({ riverTimeFrame: "" });
    }
  };

  useEffect(() => {
    dispatch(
      fetchData({
        url: urlTreatType,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlWtrSrce,
        limit: null,
        start: 0,
      })
    );

    dispatch(
      fetchData({
        url: urlCrop,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlPathway,
        limit: null,
        start: 0,
      })
    );
    dispatch(
      fetchData({
        url: urlAgency,
        limit: null,
        start: 0,
      })
    );

    dispatch(fetchSubsidySource());
  }, []);

  const initialFormValues = {
    ...activeObjRiverTraining,
    riverStartDate: activeObjRiverTraining?.riverStartDate
      ? dayjs(new Date(activeObjRiverTraining?.riverStartDate))
      : null,
    riverEndDate: activeObjRiverTraining?.riverEndDate
      ? dayjs(new Date(activeObjRiverTraining?.riverEndDate))
      : null,
  };

  useEffect(() => {
    form.setFieldsValue({
      ...activeObjRiverTraining,
      riverStartDate: activeObjRiverTraining?.riverStartDate
        ? dayjs(new Date(activeObjRiverTraining?.riverStartDate))
        : null,
      riverEndDate: activeObjRiverTraining?.riverEndDate
        ? dayjs(new Date(activeObjRiverTraining?.riverEndDate))
        : null,
    });
    if (activeObjRiverTraining?.riverSourceFundsId) {
      form.setFieldsValue({
        riverSourceFundsId: activeObjRiverTraining?.riverSourceFundsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjRiverTraining?.riverCropsId) {
      form.setFieldsValue({
        riverCropsId: activeObjRiverTraining?.riverCropsId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
    if (activeObjRiverTraining?.riverPathwayId) {
      form.setFieldsValue({
        riverPathwayId: activeObjRiverTraining?.riverPathwayId
          ?.split(",")
          ?.map((b) => b?.trim()),
      });
    }
  }, [activeObjRiverTraining, form]);

  useEffect(() => {
    if (activeObjRiverTraining?.riverId) {
      dispatch(
        fetchSubCounties({
          subCountyId: activeObjRiverTraining?.riverCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlConstituency,
          constCountyId: activeObjRiverTraining?.riverCountyId,
        })
      );
      dispatch(
        fetchData({
          url: urlWard,
          wdConstId: activeObjRiverTraining?.riverConstId,
        })
      );
      dispatch(
        fetchLocation({
          locSubId: activeObjRiverTraining?.riverSubCountyId,
        })
      );
      dispatch(
        fetchSubLocation({
          slocLocId: activeObjRiverTraining?.riverLocationId,
        })
      );
    }
  }, [activeObjRiverTraining, form]);

  return (
    <div className="w-full ">
      <BreadCrumb
        breadcrumbs={[
          { title: "River Training", path: "/rob/river-training" },
          { title: "River Training Form" },
        ]}
      />
      <div className="white_card mt-[2.06rem]">
        <div className="form-add-h mb-[1.25rem]">
          River Training Information
        </div>
        <Divider />
        <div className="w-full mt-[1.75rem]">
          <Form
            layout="vertical"
            ref={formRef}
            name="control-ref"
            onFinish={onFinish}
            className=""
            style={{
              maxWidth: "100%",
            }}
            form={form}
            initialValues={initialFormValues}
            onValuesChange={handleDatesChange}
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-10">
              <Form.Item
                name="riverName"
                label={"Project Name"}
                rules={[
                  {
                    required: true,
                    message: "Please add project name",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
              <Form.Item
                name="riverCountyId"
                label="County"
                rules={[{ required: true, message: "Please select county" }]}
              >
                <Select
                  allowClear
                  options={counties?.map((c) => ({
                    value: c?.countyId,
                    label: c?.countyName,
                  }))}
                  onChange={handleRiverCountyChange}
                  showSearch
                  filterOption={(input, option) =>
                    option?.label?.toLowerCase()?.includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              {riverCountyId && (
                <>
                  <Form.Item
                    name="riverConstId"
                    label="Constituency"
                    rules={[
                      { required: true, message: "Please select constituency" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={dataConstituency?.map((c) => ({
                        value: c?.constId,
                        label: c?.constName,
                      }))}
                      onChange={handleRiverConstituencyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="riverSubCountyId"
                    label="Subcounty"
                    rules={[
                      { required: true, message: "Please select subcounty" },
                    ]}
                  >
                    <Select
                      allowClear
                      options={subCounties?.map((s) => ({
                        value: s?.subId,
                        label: s?.subCountyName,
                      }))}
                      onChange={handleRiverSubCountyChange}
                      showSearch
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          ?.includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </>
              )}

              {riverSubCountyId && (
                <Form.Item
                  name="riverLocationId"
                  label="Location"
                  rules={[
                    { required: true, message: "Please select location" },
                  ]}
                >
                  <Select
                    allowClear
                    options={locations?.map((l) => ({
                      value: l?.locId,
                      label: l?.locLocation,
                    }))}
                    onChange={handleRiverLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {riverLocationId && (
                <Form.Item
                  name="riverSubLocationId"
                  label="Sublocation"
                  rules={[
                    { required: true, message: "Please select sublocation" },
                  ]}
                >
                  <Select
                    allowClear
                    options={subLocations?.map((sl) => ({
                      value: sl?.slocId,
                      label: sl?.slocLocation,
                    }))}
                    onChange={handleRiverSubLocationChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              {riverConstId && (
                <Form.Item
                  name="riverWardId"
                  label="Ward"
                  rules={[{ required: true, message: "Please select ward" }]}
                >
                  <Select
                    allowClear
                    options={dataWard?.map((w) => ({
                      value: w?.wdId,
                      label: w?.wdWard,
                    }))}
                    onChange={handleRiverWardChange}
                    showSearch
                    filterOption={(input, option) =>
                      option?.label
                        ?.toLowerCase()
                        ?.includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              )}

              <Form.Item
                name="riverLat"
                label={"GPS Latitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add latitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverLong"
                label={"GPS Longitude (Decimal degrees, WGS84)"}
                rules={[
                  {
                    required: true,
                    message: "Please add longitude",
                  },
                ]}
              >
                <InputNumber
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Ksh. e.g 500"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverCost"
                label={"Cost"}
                rules={[
                  {
                    required: true,
                    message: "Please add cost",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverTrainedLength"
                label={"Length of River Drained"}
                rules={[
                  {
                    required: true,
                    message: "Please add length of river drained",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>M</span>}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverHeight"
                label={"Height"}
                rules={[
                  {
                    required: true,
                    message: "Please add height",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>M</span>}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverGabionsNo"
                label={"No of Gabions Used"}
                rules={[
                  {
                    required: true,
                    message: "Please add No of gabions used",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>M</span>}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverAcreageProtected"
                label={"Acreage Protected"}
                rules={[
                  {
                    required: true,
                    message: "Please add acreage protected",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>Acres</span>}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverAcreageFarmed"
                label={"Acreage Farmed"}
                rules={[
                  {
                    required: true,
                    message: "Please add acreage farmed",
                  },
                ]}
              >
                <InputNumber
                  suffix={<span>Acres</span>}
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>

              <Form.Item
                label={"Crops type"}
                name="riverCropsId"
                rules={[{ required: true, message: "Required field" }]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following crops
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataCrop?.length > 0 &&
                    dataCrop?.map((item) => (
                      <Select.Option key={item?.cropId} value={item?.cropId}>
                        {item?.cropName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="riverEstBeneficiaries"
                label={"Estimated Benaficiaries"}
                rules={[
                  {
                    required: true,
                    message: "Please add benaficiaries",
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                  parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                  placeholder="Enter value"
                  className="input flex"
                />
              </Form.Item>
              <Form.Item
                name="riverStatus"
                className=""
                label={"River Training status"}
                rules={[
                  {
                    required: true,
                    message: "Please select river training status",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    rvrTrainingStatusData?.length > 0 &&
                    rvrTrainingStatusData?.map((item) => ({
                      value: item?.value,
                      label: item?.label,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>
              <Form.Item
                label="Start Date"
                name="riverStartDate"
                rules={[
                  {
                    required: true,
                    message: "Start date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="End Date"
                name="riverEndDate"
                rules={[
                  {
                    required: true,
                    message: "End date is required",
                  },
                ]}
              >
                <DatePicker format={"YYYY MMM DD"} className="input" />
              </Form.Item>
              <Form.Item
                label="Time Frame"
                name="riverTimeFrame"
                rules={[
                  {
                    required: true,
                    message: "Time Frame date is required",
                  },
                ]}
              >
                <Input disabled className="input" />
              </Form.Item>
              <Form.Item
                label="Source of Funding"
                name="riverSourceFundsId"
                rules={[
                  { required: true, message: "Source of Funding is required" },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following sources
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  tagRender={(props) => {
                    const { label, closable, onClose } = props;
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px 8px",
                          margin: "2px",
                          borderRadius: "4px",
                          background: "#f5f5f5",
                        }}
                      >
                        {label}
                        {closable && (
                          <span
                            onClick={onClose}
                            style={{
                              marginLeft: "8px",
                              cursor: "pointer",
                              color: "red",
                            }}
                          >
                            ×
                          </span>
                        )}
                      </span>
                    );
                  }}
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {subsidySources?.map((item) => (
                    <Select.Option
                      key={item?.subsidySourceId}
                      value={item?.subsidySourceId}
                    >
                      {item?.subsidySourceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label={"Pathways"}
                name="riverPathwayId"
                rules={[
                  {
                    required: true,
                    message: "Please add pathways",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  dropdownRender={(menu) => (
                    <>
                      <Space style={{ padding: "8px 8px 4px" }}>
                        <span className="select-info mt-1">
                          Choose one or more of the following pathways
                        </span>
                      </Space>
                      <Divider style={{ margin: "8px 0" }} />
                      {menu}
                    </>
                  )}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    )
                  }
                  dropdownStyle={{ maxHeight: 400, overflowY: "auto" }}
                >
                  {dataPathway?.length > 0 &&
                    dataPathway?.map((item) => (
                      <Select.Option
                        key={item?.irrigationPathwayId}
                        value={item?.irrigationPathwayId}
                      >
                        {item?.irrigationPathwayName}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="riverAgencyId"
                className=""
                label={"Implementing Entity/Department"}
                rules={[
                  {
                    required: true,
                    message: "Please select Implementing Entity/Department",
                  },
                ]}
              >
                <Select
                  className=""
                  allowClear
                  style={{
                    width: "100%",
                  }}
                  options={
                    dataAgency?.length > 0 &&
                    dataAgency?.map((item) => ({
                      value: item?.agencyId,
                      label: item?.agencyName,
                    }))
                  }
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label?.toLocaleLowerCase() ?? "").includes(
                      input?.toLocaleLowerCase()
                    )
                  }
                />
              </Form.Item>

              <Form.Item
                name="riverManagingAgency"
                label={"Managing Entity"}
                rules={[
                  {
                    required: true,
                    message: "Please add Managing Entity",
                  },
                ]}
              >
                <Input className="input" />
              </Form.Item>
            </div>
            <Form.Item
              name="riverRemarks"
              label={"Remarks"}
              rules={[
                {
                  required: true,
                  message: "Please add remarks",
                },
              ]}
            >
              <TextArea
                placeholder="Description..."
                rows={5}
                className="text_area"
              />
            </Form.Item>
            <div className="flex justify-between mt-[48px] ">
              <div className="justify-start"></div>
              <div className="justify-end flex items-center mb-[58px] gap-x-5">
                <div className="w-[150px]">
                  <button
                    type="button"
                    key="back"
                    onClick={handleCancel}
                    className="cstm-btn"
                  >
                    Cancel
                  </button>
                </div>

                <div className="w-[150px]">
                  <button key="submit" type="submit" className="cstm-btn-2">
                    {saving ? (
                      <Spin />
                    ) : activeObjRiverTraining?.riverId ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};
export default RiverTrainingAdd;
